/**
 * Presents validation issues to an admin: classify (blocker vs fixable), name
 * (`definition.screens.2.content.0.alt` → "Chapter 3 · Content block 1" / "Image description";
 * `definition.exercises.1.options.0.text` → "Exercise 2" / "Option 1 → Text"), group by owning node.
 * A trimmed copy of `model/assessments/validation-report.hook.ts` without rubrics.
 */
import { computed, toValue } from 'vue'

import type { MaybeRefOrGetter } from 'vue'
import type { AssessmentValidationBlocker } from '../assessments/interfaces'
import type { AssessmentValidationGroup } from '../assessments/interfaces'
import type { AssessmentValidationGroupIssue } from '../assessments/interfaces'
import type { AssessmentValidationIssue } from '../assessments/interfaces'
import type { AssessmentValidationTarget } from '../assessments/interfaces'
import type { UseValidationReportReturn } from '../assessments/interfaces'

import { withCount } from '@shared/lib/format'

/** The only blocker: a `schemaVersion` this editor cannot author. */
const isBlockerPath = (segments: (string | number)[]): boolean =>
  segments.at(-1) === 'schemaVersion'

const humanize = (segment: string): string => {
  const spaced = segment.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ')

  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase()
}

/** `cta` / `isLast` / `layout` are absent on purpose: the editor renders no control for them. */
const FIELD_LABELS: Record<string, string> = {
  title: 'Title',
  description: 'Description',
  disciplineCode: 'Discipline',
  grade: 'Grade',
  value: 'Text',
  text: 'Text',
  url: 'Image file',
  alt: 'Image description',
  type: 'Type',
  id: 'Id',
  // math exercises
  prompt: 'Prompt',
  hint: 'Hint',
  explanation: 'Explanation',
  difficulty: 'Difficulty',
  answer: 'Answer',
  correctOptionId: 'Correct option',
  correctTickId: 'Correct tick',
  left: 'Left side',
  right: 'Right side',
  label: 'Label',
  exercises: 'Exercises',
}

/** An exercise's id-keyed lists: `options.1` reads "Option 2", not "Options → #2". */
const LIST_ITEM_LABELS: Record<string, string> = {
  options: 'Option',
  pairs: 'Pair',
  items: 'Item',
  ticks: 'Tick',
  steps: 'Step',
}

const fieldLabel = (segment: string | number): string =>
  typeof segment === 'number'
    ? `#${segment + 1}`
    : FIELD_LABELS[segment] ?? humanize(segment)

/** A chapter is stored as a screen. */
const OWNER_LABELS: Record<string, string> = {
  screens: 'Chapter',
  content: 'Content block',
  activities: 'Activity',
  exercises: 'Exercise',
  prompt: 'Prompt block',
}

interface Split {
  key: string
  label: string
  field: string
}

/** `definition` is dropped from the label but kept in the key. */
const splitPath = (segments: (string | number)[]): Split => {
  const owners: string[] = []
  const keyParts: (string | number)[] = []
  const fields: string[] = []
  let ownerEnd = 0

  const body = segments[0] === 'definition' ? segments.slice(1) : segments
  const prefix = segments[0] === 'definition' ? ['definition'] : []

  for (let index = 0; index < body.length; index += 1) {
    const segment = body[index]
    const next = body[index + 1]

    if (typeof segment !== 'string') {
      break
    }

    const ownerLabel = OWNER_LABELS[segment]

    if (ownerLabel !== undefined && typeof next === 'number') {
      owners.push(`${ownerLabel} ${next + 1}`)
      keyParts.push(segment, next)
      ownerEnd = index + 2
      index += 1
      continue
    }
    break
  }

  const rest = body.slice(ownerEnd)

  rest.forEach((segment, index) => {
    // "Image file #2" beats "Image file → File → #2".
    if (typeof segment === 'string' && OWNER_LABELS[segment] && fields.length > 0) {
      return
    }
    const previous = rest[index - 1]

    // Folded into the list label that precedes it.
    if (typeof segment === 'number' && typeof previous === 'string' && LIST_ITEM_LABELS[previous]) {
      return
    }
    const next = rest[index + 1]

    if (typeof segment === 'string' && LIST_ITEM_LABELS[segment] && typeof next === 'number') {
      fields.push(`${LIST_ITEM_LABELS[segment]} ${next + 1}`)
      return
    }
    fields.push(fieldLabel(segment))
  })

  return {
    key: [...prefix, ...keyParts].join('.'),
    label: owners.join(' · '),
    field: fields.join(' → '),
  }
}

interface UseAssignmentValidationReportReturn extends UseValidationReportReturn {
  /** The exercise an issue belongs to, for a MATH document; `null` when it is document-level. */
  resolveExerciseId: (issue: AssessmentValidationIssue) => string | null
  /** The issue sits in an exercise's prompt — the one field the prompt's Preview tab hides. */
  isPromptIssue: (issue: AssessmentValidationIssue) => boolean
}

/** `document` is the raw working document — a document with issues never parsed. */
export const useAssignmentValidationReport = (
  issues: MaybeRefOrGetter<AssessmentValidationIssue[]>,
  document: MaybeRefOrGetter<unknown>,
): UseAssignmentValidationReportReturn => {
  const refIssues = computed<AssessmentValidationIssue[]>(() => toValue(issues))

  const refBlockers = computed<AssessmentValidationBlocker[]>(() => {
    const byLabel = new Map<string, AssessmentValidationBlocker>()

    refIssues.value.filter(issue => isBlockerPath(issue.segments)).forEach((issue) => {
      // First message wins.
      if (!byLabel.has('This assignment')) {
        byLabel.set('This assignment', { label: 'This assignment', message: issue.message })
      }
    })

    return [...byLabel.values()]
  })

  const refGroups = computed<AssessmentValidationGroup[]>(() => {
    const byKey = new Map<string, AssessmentValidationGroup>()

    refIssues.value.filter(issue => !isBlockerPath(issue.segments)).forEach((issue) => {
      const split = splitPath(issue.segments)
      const key = split.key || 'definition'
      const label = split.label || 'Assignment'

      const group = byKey.get(key) ?? { key, label, issues: [] }
      const entry: AssessmentValidationGroupIssue = { ...issue, field: split.field }

      // Zod may report the same field/message twice.
      if (!group.issues.some(existing =>
        existing.field === entry.field && existing.message === entry.message)) {
        group.issues.push(entry)
      }
      byKey.set(key, group)
    })

    return [...byKey.values()]
  })

  const refHasProblems = computed<boolean>(() =>
    refBlockers.value.length > 0 || refGroups.value.length > 0,
  )

  /** Counts groups, not blockers — so `null` is not "all clear". */
  const refSummary = computed<string | null>(() => {
    const groups = refGroups.value.length

    return groups === 0
      ? null
      : `${withCount(groups, { one: 'place', other: 'places' })} to fix`
  })

  /** Chapter only; `activityId` is always `null`. */
  const resolveTarget = (issue: AssessmentValidationIssue): AssessmentValidationTarget | null => {
    const doc = toValue(document)
    const [head] = issue.segments
    const body = head === 'definition' ? issue.segments.slice(1) : issue.segments

    if (body[0] !== 'screens' || typeof body[1] !== 'number') {
      return null
    }

    const screens = (doc as { definition?: { screens?: unknown } } | null)?.definition?.screens
    const chapter = Array.isArray(screens) ? screens[body[1]] : undefined

    if (chapter === undefined) {
      return null
    }

    return {
      screenId: (chapter as { id?: unknown }).id as string ?? null,
      activityId: null,
    }
  }

  const exerciseSegments = (issue: AssessmentValidationIssue): (string | number)[] =>
    issue.segments[0] === 'definition' ? issue.segments.slice(1) : issue.segments

  const resolveExerciseId = (issue: AssessmentValidationIssue): string | null => {
    const body = exerciseSegments(issue)

    if (body[0] !== 'exercises' || typeof body[1] !== 'number') {
      return null
    }

    const exercises = (toValue(document) as { definition?: { exercises?: unknown } } | null)?.definition?.exercises
    const exercise: unknown = Array.isArray(exercises) ? exercises[body[1]] : undefined
    const id: unknown = typeof exercise === 'object' && exercise !== null
      ? (exercise as { id?: unknown }).id
      : undefined

    return typeof id === 'string' && id ? id : null
  }

  const isPromptIssue = (issue: AssessmentValidationIssue): boolean => {
    const body = exerciseSegments(issue)

    return body[0] === 'exercises' && typeof body[1] === 'number' && body[2] === 'prompt'
  }

  return {
    refBlockers,
    refGroups,
    refSummary,
    refHasProblems,
    resolveTarget,
    resolveExerciseId,
    isPromptIssue,
  }
}
